'use client';

/**
 * The blue D: an ex-dividend date from 2 trading days before through 1
 * trading day after (rule in @/lib/dividendBadge, data from the shared
 * useDividendSoonSymbols map, so every badge on screen agrees and they all
 * share one request).
 *
 * - DividendGlyph        — the round D, given the info (TickerBadges uses it
 *                          after checking the map itself).
 * - DividendBadge        — the round D for a ticker; watchlist rows.
 * - DividendHeaderBadge  — the stock / ETF header pill, "D Ex-div Mon".
 *
 * Round and sky blue to match the chart's D event circles; the dilution badge
 * is the red square "D", so the two never read as the same mark. Dashed when
 * the date is projected from the issuer's cadence rather than announced.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { useDividendSoonSymbols } from '@/hooks/useDividendSoonSymbols';
import {
  dividendBadgePhase, dividendBadgeWhen, formatDividendBadgeTooltip,
  type DividendBadgePhase, type DividendSoon,
} from '@/lib/dividendBadge';

/** Brightest on the ex-date itself, muted the day after it went ex. Text is
 *  sky-100 / sky-300 because globals.css remaps exactly those to dark sky in
 *  the light theme (an opacity-suffixed text class would slip past that remap
 *  and wash out on white), and the muting is done with opacity instead. */
export const DIVIDEND_PHASE_CLASSES: Record<DividendBadgePhase, string> = {
  upcoming: 'bg-sky-500/15 text-sky-300 border-sky-500/50',
  today:    'bg-sky-500/35 text-sky-100 border-sky-400/80',
  after:    'bg-sky-500/10 text-sky-300 border-sky-500/30 opacity-80',
};

export function DividendGlyph({ info, className }: { info: DividendSoon; className?: string }) {
  const phase = dividendBadgePhase(info.exDate);
  if (!phase) return null;
  const label = formatDividendBadgeTooltip(info);
  return (
    <span
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex h-4 w-4 items-center justify-center rounded-full border text-[9px] font-bold leading-none flex-shrink-0',
        DIVIDEND_PHASE_CLASSES[phase],
        info.projected && 'border-dashed',
        className,
      )}
    >
      D
    </span>
  );
}

export function DividendBadge({ ticker, className }: { ticker: string; className?: string }) {
  const info = useDividendSoonSymbols().get((ticker || '').toUpperCase());
  return info ? <DividendGlyph info={info} className={className} /> : null;
}

export function DividendHeaderBadge({ ticker }: { ticker: string }) {
  const info = useDividendSoonSymbols().get((ticker || '').toUpperCase());
  const phase = info ? dividendBadgePhase(info.exDate) : null;
  const when = info ? dividendBadgeWhen(info.exDate) : null;
  if (!info || !phase || !when) return null;
  return (
    <span
      title={formatDividendBadgeTooltip(info)}
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-px rounded border text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap',
        DIVIDEND_PHASE_CLASSES[phase],
        info.projected && 'border-dashed',
      )}
    >
      <span className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-current text-[7px] font-bold leading-none" aria-hidden="true">
        D
      </span>
      {when}
    </span>
  );
}

export default DividendBadge;
